import { ReactComponent as CloseSvg } from "@/src/assets/icons/icon-close-red.svg";
import { ReactComponent as InfoSvg } from "@/src/assets/icons/icon-Info-blue4-fill.svg";
import { Button } from "@/src/components/atom/Button";
import Typo from "@/src/components/atom/Typo";
import CallOut from "@/src/components/molecule/CallOut";
import Flex from "@/src/components/molecule/Flex";
import { authApi } from "@/src/store/apis/auth";
import colorSet, { ColorType } from "@/src/styles/color";
import { isNull, isUndefined } from "@/src/utils/is";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import PaymentMethodChangeDialog from "./dialog/PaymentMethodChangeDialog";
import { localstorageKeySet } from "@/src/constant/localstorage";
import { subscriptionApi } from "@/src/store/apis/subscription";
import { useTranslation } from "react-i18next";

type CreditCalloutType = "BUY_CREDIT_FAIL" | null;
enum DialogState {
  NULL,
  PAYMENT_METHOD_CHANGE,
}

function CreditCallout() {
  const { t } = useTranslation();
  const [calloutStatus, setCalloutStatus] = useState<CreditCalloutType>(null);
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);

  const creditPaymentFailCallOutClose = localStorage.getItem(
    localstorageKeySet.CREDIT_PAYMENT_FAIL_CALL_OUT_CLOSE
  );

  // API
  const { failReason, paymentStatus } =
    subscriptionApi.endpoints.getPayments.useQueryState(
      {
        page: 1,
        pageSize: 1,
        paymentType: "CREDIT",
      },
      {
        selectFromResult: ({ currentData, isError, isFetching }) => {
          const isUnstable = isError || isFetching || isUndefined(currentData);
          const isStable = !isUnstable;

          return {
            failReason: isStable ? currentData.rows[0].failReason || "" : "",
            paymentStatus: currentData?.rows[0].paymentStatus,
          };
        },
      }
    );
  const { isCorporateManager } = authApi.endpoints.getSession.useQueryState(
    undefined,
    {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isCorporateManager: isStable
            ? currentData.row.exporterUserType === "CORPORATE_MANAGER"
            : false,
        };
      },
    }
  );

  const getCalloutStatus = useCallback(() => {
    /**
     * - 크레딧 결제 실패
     * - 기업관리자만 노출
     * - 로컬스토리지내 크레딧 결제 실패 콜아웃 닫기 상태가 없을때
     * - 가장 최근의 결제실패 사유가 있을때
     * - 결제상태가 결제실패
     */
    if (
      true &&
      isCorporateManager &&
      isNull(creditPaymentFailCallOutClose) &&
      !!failReason &&
      paymentStatus === "PAYMENT_FAIL"
    ) {
      return setCalloutStatus("BUY_CREDIT_FAIL");
    }
  }, [
    creditPaymentFailCallOutClose,
    failReason,
    isCorporateManager,
    paymentStatus,
  ]);

  const handleCloseCallout = () => {
    localStorage.setItem(
      localstorageKeySet.CREDIT_PAYMENT_FAIL_CALL_OUT_CLOSE,
      "true"
    );
    setCalloutStatus(null);
  };

  const renderCallout = () => {
    switch (calloutStatus) {
      case "BUY_CREDIT_FAIL":
        return (
          <StyledCallOut borderColor="red7" backgroundColor="red8">
            <InfoIcon color="red2" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
              gap={8}
            >
              <Typo color="red2" typoType="b9r">
                {t("common:paymentFailReason")} : {failReason}
              </Typo>

              <ButtonContainer alignItems="center" gap={8}>
                <Button
                  buttonColor="black"
                  buttonGrade="tertiary"
                  buttonSize={32}
                  onClick={() =>
                    setDialogState(DialogState.PAYMENT_METHOD_CHANGE)
                  }
                >
                  {t("common:paymentMethodChangeCallout")}
                </Button>
                <CloseIcon color="red2" onClick={handleCloseCallout} />
              </ButtonContainer>
            </Flex>
          </StyledCallOut>
        );
      case null:
      default:
        return <></>;
    }
  };

  const renderDialog = () => {
    switch (dialogState) {
      case DialogState.PAYMENT_METHOD_CHANGE:
        return (
          <PaymentMethodChangeDialog
            open={dialogState === DialogState.PAYMENT_METHOD_CHANGE}
            onOpenChange={() => setDialogState(DialogState.NULL)}
          />
        );
    }
  };

  useEffect(() => {
    getCalloutStatus();
  }, [getCalloutStatus, creditPaymentFailCallOutClose]);

  return (
    <CalloutContainer $isCallout={!isNull(calloutStatus)}>
      {renderCallout()}
      {renderDialog()}
    </CalloutContainer>
  );
}

export default CreditCallout;

const CalloutContainer = styled.div<{ $isCallout: boolean }>`
  margin-bottom: ${({ $isCallout }) => ($isCallout ? "16px" : "0px")};
`;

const StyledCallOut = styled(CallOut)`
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const InfoIcon = styled(InfoSvg)<{ color: ColorType }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  * {
    fill: ${({ color }) => colorSet[color]};
  }
`;

const CloseIcon = styled(CloseSvg)<{ color: ColorType }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;

  * {
    fill: ${({ color }) => colorSet[color]};
  }
`;

const ButtonContainer = styled(Flex)`
  flex-shrink: 0;
`;
